import type { UpdateNotificationKindSettingInput } from '@repo/api-contracts';
import { useState } from 'react';
import { useInvalidateNotifications } from '@/entities/notification';
import { trpc } from '@/shared/api';
import { getErrorMessage } from '@/shared/lib';

export interface UseUpdateNotificationKindSettingResult {
  /** 성공하면(설정을 다시 읽은 뒤) true. 서버가 거부하면 false 이고 문구는 error 에 남는다. */
  readonly updateKindSetting: (input: UpdateNotificationKindSettingInput) => Promise<boolean>;
  /** 요청과 그 뒤 설정 재조회가 끝날 때까지 true — 바뀐 값이 오기 전에 스위치를 다시 누르지 못하게 한다. */
  readonly isPending: boolean;
  readonly error: string | null;
}

const FORM_ERROR_FALLBACK = '알림 설정을 바꾸지 못했습니다.';

/**
 * 종류별 ON/OFF 바꾸기(notificationManagement.updateKindSetting, 알림 설계 N3·§6-2). 끈 종류는 worker 가 보내기 직전에 읽어
 * 다음 발송부터 보내지 않는다(이력에 SKIPPED/KIND_DISABLED). 항상-발송 4종은 화면에 스위치가 없고, 와도 서버가
 * NOTIFICATION_KIND_ALWAYS_ON 으로 거부한다.
 */
export const useUpdateNotificationKindSetting = (): UseUpdateNotificationKindSettingResult => {
  const [error, setError] = useState<string | null>(null);
  const invalidate = useInvalidateNotifications();
  const mutation = trpc.notificationManagement.updateKindSetting.useMutation({
    // 재조회 promise 를 돌려준다 — 라이브러리가 그것을 기다려, 새 값이 올 때까지 isPending 이 남는다.
    onSuccess: () => invalidate.invalidateSettings(),
  });

  const updateKindSetting = async (input: UpdateNotificationKindSettingInput): Promise<boolean> => {
    setError(null);

    try {
      await mutation.mutateAsync(input);
      return true;
    } catch (cause) {
      setError(getErrorMessage(cause, FORM_ERROR_FALLBACK));
      return false;
    }
  };

  return { updateKindSetting, isPending: mutation.isPending, error };
};
